import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import { Send, Paperclip, Mic, X, Image, Loader2, Plus } from 'lucide-react';
import { useChatStore } from '@/stores/chat-store';
import { useWebSocketStore } from '@/stores/websocket-store';
import { Button, useToast } from '@caruso/ui';
import { cn } from '@caruso/ui';
import { AudioRecorder } from './audio-recorder';
import { ImagePreview } from './image-preview';
import { AttachmentOptionsModal } from './attachment-options-modal';
import { api } from '@/lib/axios';
import { Product } from '@/services/products.service';
import { Venue } from '@caruso/types';

export function ChatInput() {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { toast } = useToast();
  const { selectedConversation, sendMessage, isSending } = useChatStore();
  const { sendTypingStart, sendTypingStop } = useWebSocketStore();

  // Obter URL base do tenant para links
  const getBaseUrl = () => {
    // TODO: Pegar do tenant config quando disponivel
    return window.location.origin;
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [message]);

  // Clear image preview when conversation changes
  useEffect(() => {
    setSelectedImage(null);
    setImagePreviewUrl(null);
  }, [selectedConversation?.id]);

  const handleTyping = () => {
    if (!selectedConversation) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Send typing start
    sendTypingStart(selectedConversation.id);

    // Set timeout to send typing stop
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStop(selectedConversation.id);
    }, 2000);
  };

  const handleSend = async () => {
    if (!selectedConversation || (!message.trim() && !selectedImage) || isSending) return;

    try {
      let mediaUrl: string | undefined;
      let mediaType: string | undefined;
      let mediaFileName: string | undefined;

      // Upload image if selected
      if (selectedImage) {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', selectedImage);

        const uploadResponse = await api.post<{ url: string; mimetype: string; filename: string }>(
          '/upload',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );

        mediaUrl = uploadResponse.data.url;
        mediaType = uploadResponse.data.mimetype;
        mediaFileName = uploadResponse.data.filename;
        setIsUploading(false);
      }

      // Send message
      await sendMessage(
        selectedConversation.id,
        message.trim() || (mediaType?.startsWith('image') ? 'Imagem' : 'Arquivo'),
        mediaUrl,
        mediaType,
        mediaFileName
      );

      // Clear state
      setMessage('');
      setSelectedImage(null);
      setImagePreviewUrl(null);

      // Stop typing indicator
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      sendTypingStop(selectedConversation.id);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsUploading(false);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel enviar a mensagem.',
        variant: 'destructive',
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Arquivo muito grande',
          description: 'O tamanho maximo permitido e 10MB.',
          variant: 'destructive',
        });
        return;
      }

      setSelectedImage(file);
      setImagePreviewUrl(URL.createObjectURL(file));
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAudioRecorded = async (audioBlob: Blob) => {
    if (!selectedConversation) return;

    try {
      setIsUploading(true);

      // Create file from blob
      const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
        type: 'audio/webm',
      });

      // Upload audio
      const formData = new FormData();
      formData.append('file', audioFile);

      const uploadResponse = await api.post<{ url: string; mimetype: string; filename: string }>(
        '/upload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      // Send audio message
      await sendMessage(
        selectedConversation.id,
        'Audio',
        uploadResponse.data.url,
        uploadResponse.data.mimetype,
        uploadResponse.data.filename
      );

      setIsRecording(false);
      setIsUploading(false);
    } catch (error) {
      console.error('Error sending audio:', error);
      setIsUploading(false);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel enviar o audio.',
        variant: 'destructive',
      });
    }
  };

  const clearImagePreview = () => {
    setSelectedImage(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
  };

  // Enviar link de pagamento (venda rapida)
  const handleSendPaymentLink = async (product: Product) => {
    if (!selectedConversation) return;

    try {
      const baseUrl = getBaseUrl();
      const paymentLink = `${baseUrl}/loja/produto/${product.id}?cliente=${selectedConversation.clientId}`;
      const priceFormatted = product.price?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00';

      const messageText = `💳 *Link de Pagamento*\n\n` +
        `*${product.name}*\n` +
        `Valor: R$ ${priceFormatted}\n\n` +
        `Clique no link abaixo para efetuar o pagamento:\n${paymentLink}`;

      await sendMessage(selectedConversation.id, messageText);

      toast({
        title: 'Link enviado',
        description: 'Link de pagamento enviado com sucesso.',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error sending payment link:', error);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel enviar o link de pagamento.',
        variant: 'destructive',
      });
    }
  };

  // Enviar link de ambiente
  const handleSendVenueLink = async (venue: Venue) => {
    if (!selectedConversation) return;

    try {
      const baseUrl = getBaseUrl();
      const venueLink = `${baseUrl}/reserva/${venue.id}?cliente=${selectedConversation.clientId}`;

      const messageText = `🏠 *Link do Ambiente*\n\n` +
        `*${venue.name}*\n` +
        `${venue.description || ''}\n\n` +
        `Clique no link abaixo para mais informacoes ou reservar:\n${venueLink}`;

      await sendMessage(selectedConversation.id, messageText);

      toast({
        title: 'Link enviado',
        description: 'Link do ambiente enviado com sucesso.',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error sending venue link:', error);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel enviar o link do ambiente.',
        variant: 'destructive',
      });
    }
  };

  const isClosed = selectedConversation?.status === 'CLOSED';

  if (isClosed) {
    return (
      <div className="p-4 border-t border-brand-gold/10 bg-brand-smoke/30">
        <p className="text-center text-sm text-brand-ivory/50">
          Esta conversa foi encerrada. Reabra para enviar mensagens.
        </p>
      </div>
    );
  }

  // Recording mode
  if (isRecording) {
    return (
      <div className="p-4 border-t border-brand-gold/10">
        <AudioRecorder
          onCancel={() => setIsRecording(false)}
          onSend={handleAudioRecorded}
          isUploading={isUploading}
        />
      </div>
    );
  }

  return (
    <>
      <div className="border-t border-brand-gold/10">
        {/* Image Preview */}
        {imagePreviewUrl && (
          <ImagePreview
            imageUrl={imagePreviewUrl}
            onClear={clearImagePreview}
          />
        )}

        {/* Input Area */}
        <div className="p-4">
          <div className="flex items-end gap-2">
            {/* Attachment Button */}
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                onChange={handleImageSelect}
                className="hidden"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowAttachmentModal(true)}
                disabled={isSending || isUploading}
                className="text-brand-ivory/50 hover:text-brand-gold"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>

          {/* Text Input */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                handleTyping();
              }}
              onKeyDown={handleKeyDown}
              placeholder="Digite uma mensagem..."
              rows={1}
              disabled={isSending || isUploading}
              className={cn(
                'w-full px-4 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-2xl',
                'text-brand-ivory placeholder:text-brand-ivory/40',
                'focus:outline-none focus:border-brand-gold/30',
                'resize-none max-h-[150px]',
                'disabled:opacity-50'
              )}
            />
          </div>

          {/* Send / Mic Button */}
          {message.trim() || selectedImage ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSend}
              disabled={isSending || isUploading}
              className="text-brand-gold hover:bg-brand-gold/10"
            >
              {isSending || isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsRecording(true)}
              disabled={isSending}
              className="text-brand-ivory/50 hover:text-brand-gold"
            >
              <Mic className="w-5 h-5" />
            </Button>
          )}
          </div>
        </div>
      </div>

      {/* Attachment Options Modal */}
      <AttachmentOptionsModal
        isOpen={showAttachmentModal}
        onClose={() => setShowAttachmentModal(false)}
        onSendPaymentLink={handleSendPaymentLink}
        onSendVenueLink={handleSendVenueLink}
        onSelectFile={() => fileInputRef.current?.click()}
      />
    </>
  );
}
